/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ajax from 'helpers/ajax'
import {ThunkAction} from 'model/store/thunk'
import {RootState} from 'helpers/store'
import {SavedTableView, SavedTableViewState} from 'types/entities/SavedTableView'
import {pushTableView, removeTableView, setTableViews,} from './TableViewsReducer'

interface FetchViewsResponse {
	table_views: SavedTableView[]
}

let fetchViewsPromise: Promise<SavedTableView[]> | null = null

export default class TableViewsAction {
    static readonly API_LIST   = '/admin/table-views/list'
    static readonly API_CREATE = '/admin/table-views/create'
    static readonly API_DELETE = '/admin/table-views/delete'

    static fetchViews = (): ThunkAction<Promise<SavedTableView[]>> => {
        return async (dispatch, getState): Promise<SavedTableView[]> => {
            const { views, hasLoaded } = (getState() as RootState).tableViewsState
            if (hasLoaded) {
                return views
            }

            if (fetchViewsPromise) {
                return fetchViewsPromise
            }

            fetchViewsPromise = ajax
                .get(TableViewsAction.API_LIST)
                .then((response: FetchViewsResponse) => {
                    const tableViews = response.table_views ?? []
                    dispatch(setTableViews(tableViews))
                    return tableViews
                })
                .finally(() => {
                    fetchViewsPromise = null
                })

            return fetchViewsPromise
        }
    }

    static saveView = (data: {
        /** When set, updates the existing view instead of creating a duplicate. */
        id?: number
        name: string
        page_name: string
        table_id: string
        state: SavedTableViewState
    }): ThunkAction<Promise<SavedTableView>> => {
        return async (dispatch, getState): Promise<SavedTableView> => {
            const seller_id = (getState() as RootState).sessionState.session?.uid
            const payload = {
                ...data,
                seller_id: seller_id ? Number(seller_id) : undefined,
            }
            // Same create endpoint as other CRM entities: pass id to update in place.
            const response = await ajax.post(
                TableViewsAction.API_CREATE,
                payload
            ) as Partial<{ view: SavedTableView; table_view: SavedTableView }> & SavedTableView
            // Backend may return the view under different keys or as the root object
            const view: SavedTableView = response?.view ?? response?.table_view ?? response
            dispatch(pushTableView(view))
            return view
        }
    }

    static deleteView = (id: number): ThunkAction<Promise<void>> => {
        return async (dispatch): Promise<void> => {
            await ajax.delete(`${TableViewsAction.API_DELETE}/${id}`)
            dispatch(removeTableView(id))
        }
    }
}
